import React from 'react';
import get from 'lodash/get';
import map from 'lodash/map';
import mapValues from 'lodash/mapValues';
import { Select, Button, Tag, Input, DatePicker } from 'antd';
import isEqual from 'lodash/isEqual';
import isArray from 'lodash/isArray';
import moment from 'moment';
import { hasAnyPermission } from '../../utilities/helpers';

const { Option } = Select;
const { RangePicker } = DatePicker;

const TableFilters = ({
  filtersList,
  tableData,
  fetchTableData,
  tempFilter,
  setTempFilter,
  fetchDependentOptions,
  roleCode,
}) => {
  const dateFormat = 'YYYY/MM/DD';

  const removeFilterChip = (tempFilterChipsValueKey, record) => {
    const tempFilterChipsRecord = get(
      tempFilter,
      `tempFilterChips.${tempFilterChipsValueKey}`
    ).filter((obj) => !isEqual(obj.value, record.value));

    const filterValuesRecord = isArray(
      get(tempFilter, `filterValues.${tempFilterChipsValueKey}`)
    )
      ? get(tempFilter, `filterValues.${tempFilterChipsValueKey}`).filter(
          (value) => !isEqual(value, record.value)
        )
      : [get(tempFilter, `filterValues.${tempFilterChipsValueKey}`)].filter(
          (value) => !isEqual(value, record.value)
        );

    setTempFilter({
      ...tempFilter,
      tempFilterChips: {
        ...get(tempFilter, 'tempFilterChips'),
        [tempFilterChipsValueKey]: tempFilterChipsRecord,
      },
      filterValues: {
        ...get(tempFilter, 'filterValues'),
        [tempFilterChipsValueKey]: filterValuesRecord.length
          ? filterValuesRecord
          : undefined,
      },
    });

    fetchTableData(
      get(tableData, 'pagination.current'),
      {
        ...get(tempFilter, 'filterValues'),
        [tempFilterChipsValueKey]: filterValuesRecord.length
          ? filterValuesRecord
          : undefined,
      },
      get(tableData, 'sorters'),
      get(tempFilter, 'tempFilterChips')
    );
  };

  return (
    <>
      <div className="filter-boxes">
        <div className="d-flex filter-items">
          {map(filtersList, (filterObj, index) => {
            const {
              type,
              subType,
              multipleMode,
              placeholder,
              addonBefore,
              options,
              optionLabelKey,
              optionValueKey,
              dropdownKey,
              chipValueKey,
              isAnyFilterDependent,
              permissions,
            } = filterObj;

            return (
              <React.Fragment key={index}>
                {hasAnyPermission(permissions, [roleCode]) && type === 'Input' && (
                  <div>
                    <Input
                      allowClear
                      addonBefore={addonBefore}
                      value={get(tempFilter, `filterValues.${chipValueKey}`)}
                      style={{
                        width: '150px',
                      }}
                      onChange={(e) => {
                        const inputValue =
                          e.target.value === '' ? undefined : e.target.value;
                        setTempFilter({
                          ...tempFilter,
                          filterValues: {
                            ...get(tempFilter, 'filterValues'),
                            [chipValueKey]:
                              subType === 'number' && Number(inputValue) > 0
                                ? Number(inputValue)
                                : inputValue,
                          },
                        });
                      }}
                    />
                  </div>
                )}
                {hasAnyPermission(permissions, [roleCode]) &&
                  type === 'DatePicker' && (
                    <div>
                      <RangePicker
                        value={
                          get(tempFilter, `filterValues.${chipValueKey}`)
                            ? [
                                moment(
                                  get(
                                    tempFilter,
                                    `filterValues.${chipValueKey}.from`
                                  ),
                                  dateFormat
                                ),
                                moment(
                                  get(
                                    tempFilter,
                                    `filterValues.${chipValueKey}.to`
                                  ),
                                  dateFormat
                                ),
                              ]
                            : undefined
                        }
                        size="large"
                        format="DD/MM/YYYY"
                        style={{
                          width: '200px',
                          height: '32px',
                        }}
                        onChange={(value) => {
                          setTempFilter({
                            ...tempFilter,
                            tempFilterChips: {
                              ...get(tempFilter, 'tempFilterChips'),
                              [chipValueKey]: [
                                {
                                  key: 1,
                                  children: `${get(value, '0').format(
                                    'DD-MM-YYYY'
                                  )} - ${get(value, '1').format('DD-MM-YYYY')}`,
                                  value: {
                                    from: get(value, '0').format('YYYY-MM-DD'),
                                    to: get(value, '1').format('YYYY-MM-DD'),
                                  },
                                },
                              ],
                            },
                            filterValues: {
                              ...get(tempFilter, 'filterValues'),
                              [chipValueKey]: {
                                from: get(value, '0').format('YYYY-MM-DD'),
                                to: get(value, '1').format('YYYY-MM-DD'),
                              },
                            },
                          });
                        }}
                      />
                    </div>
                  )}
                {hasAnyPermission(permissions, [roleCode]) &&
                  type === 'Select' &&
                  (multipleMode ? (
                    <div>
                      <Select
                        mode="multiple"
                        showArrow
                        showSearch
                        value={
                          get(tempFilter, `filterDropdown.${dropdownKey}`)
                            ? get(tempFilter, `filterValues.${chipValueKey}`)
                            : undefined
                        }
                        placeholder={placeholder}
                        style={{
                          width: '150px',
                        }}
                        filterOption={(input, option) =>
                          (
                            get(option, 'children').toLowerCase() ?? ''
                          ).includes(input.toLowerCase())
                        }
                        tagRender={() => null}
                        onDropdownVisibleChange={(open) => {
                          setTempFilter({
                            ...tempFilter,
                            filterDropdown: {
                              ...get(tempFilter, 'filterDropdown'),
                              [dropdownKey]: open,
                            },
                          });
                        }}
                        onChange={(value, option) => {
                          setTempFilter({
                            ...tempFilter,
                            tempFilterChips: {
                              ...get(tempFilter, 'tempFilterChips'),
                              [chipValueKey]: option,
                            },
                            filterValues: {
                              ...get(tempFilter, 'filterValues'),
                              [chipValueKey]: value.length ? value : undefined,
                            },
                          });
                          if (isAnyFilterDependent) {
                            fetchDependentOptions(value);
                          }
                        }}
                      >
                        {map(options, (option, childIndex) => (
                          <Option
                            key={childIndex}
                            value={get(option, `${optionValueKey}`)}
                          >
                            {get(option, `${optionLabelKey}`)}
                          </Option>
                        ))}
                      </Select>
                    </div>
                  ) : (
                    <div>
                      <Select
                        placeholder={placeholder}
                        value={get(tempFilter, `filterValues.${chipValueKey}`)}
                        style={{
                          width: '150px',
                        }}
                        onChange={(value) => {
                          setTempFilter({
                            ...tempFilter,
                            filterValues: {
                              ...get(tempFilter, 'filterValues'),
                              [chipValueKey]: value,
                            },
                          });
                        }}
                      >
                        {map(options, (option, childIndex) => (
                          <Option
                            key={childIndex}
                            value={get(option, `${optionValueKey}`)}
                          >
                            {get(option, `${optionLabelKey}`)}
                          </Option>
                        ))}
                      </Select>
                    </div>
                  ))}
              </React.Fragment>
            );
          })}
        </div>

        {get(tableData, 'filterChips') && (
          <div className="d-flex filter-items mt-10">
            {map(Object.keys(get(tableData, 'filterChips')), (chip) =>
              map(get(tableData, `filterChips.${chip}`), (chipObj) => (
                <Tag
                  key={get(chipObj, 'key')}
                  closable
                  color="#009ea8"
                  style={{ padding: '3px 7px' }}
                  onClose={() => {
                    removeFilterChip(chip, chipObj);
                  }}
                >
                  {get(chipObj, 'children')}
                </Tag>
              ))
            )}
          </div>
        )}
      </div>

      <div className="d-flex filter-btns">
        <Button
          className="default-btn"
          block
          onClick={() => {
            setTempFilter({
              filterDropdown: mapValues(
                get(tempFilter, 'filterDropdown'),
                () => false
              ),
              tempFilterChips: mapValues(
                get(tempFilter, 'tempFilterChips'),
                () => undefined
              ),
              filterValues: mapValues(
                get(tempFilter, 'filterValues'),
                () => undefined
              ),
            });
            fetchTableData(1, { name: get(tableData, 'filters.name') });
          }}
        >
          Reset
        </Button>
        <Button
          type="primary"
          block
          onClick={() => {
            fetchTableData(
              get(tableData, 'pagination.current'),
              {
                name: get(tableData, 'filters.name'),
                ...get(tempFilter, 'filterValues'),
              },
              get(tableData, 'sorters'),
              get(tempFilter, 'tempFilterChips')
            );
          }}
        >
          Apply Filter
        </Button>
      </div>
    </>
  );
};

export default TableFilters;
